/**
 * One tracker per bookmark set for the life of the document, and the wiring
 * of the add-in's anchor engine: the Office.js document, the HTTP resolver
 * client and the change ledger they share. `anchorTrackers` is the one
 * instance the live proposal and Apply use; tests build their own registry
 * over fixtures.
 */
import { type BookmarkedPages, OoxmlRange } from "@common/utils/redline/ooxml-range";
import type { AcceptedTextSpan } from "@common/utils/redline/redline-engine";
import { ON_PARAGRAPH, type RedlinePlacement } from "@common/utils/redline/wire/redline-placement";

import { timedBulkApplySpan, timedBulkApplySpanSync } from "../bulk-apply-run-timeline";
import { AgentDocumentViews, DEFAULT_AGENT_DOCUMENT_VIEWS_CONFIG } from "./agent-document-views";
import { AnchorLog } from "./anchor-log";
import { AnchorTracker, type AnchorDeps, type AttachedRedline } from "./anchor-tracker";
import { DocumentChangeLedger } from "./document-change-ledger";
import { DocumentPageStore } from "./document-page-store";
import { editedParagraphDetector } from "./office/edited-paragraph-detector";
import { HttpReanchorClient } from "./office/http-reanchor-client";
import { OfficeWordDocument } from "./office/office-word-document";
import type { RecoveryTrigger } from "./recovery/recovery-chain";
import { DEFAULT_RECOVERY_CONFIG, type RecoveryConfig } from "./recovery-config";
import type { WordDocument } from "./word-document";

export class AnchorTrackerRegistry {
    private readonly trackers = new Map<string, AnchorTracker>();

    constructor(
        private readonly deps: AnchorDeps,
        private readonly config: RecoveryConfig,
    ) {}

    /** The Word document the trackers read; the live proposal subscribes to its paragraph events. */
    get word(): WordDocument {
        return this.deps.word;
    }

    /**
     * Where the tracker of these bookmarks has them written, read without creating one.
     *
     * WHY
     *   Every card on the same bookmarks shares one tracker. When a card that writes its
     *   paragraph back has its deleted paragraph placed beside a neighbour, the bookmarks sit
     *   on that neighbour; a comment on them asks this, and reads its own paragraph as gone,
     *   never as the neighbour.
     *
     * CASES
     *   - a tracker follows these bookmarks -> the placement it holds
     *   - none does                         -> on_paragraph: nothing has moved them
     *
     * EXAMPLE
     *   remedy on deleted 5.1 placed after "5. Payment", the Issue's comment on the same bookmark
     *   -> placementOf([_payment_days]) = after_paragraph
     */
    placementOf(bookmarkNames: readonly string[]): RedlinePlacement {
        return this.trackers.get(AnchorTrackerRegistry.keyOf(bookmarkNames))?.placement ?? ON_PARAGRAPH;
    }

    /**
     * The part of each paragraph of a read that the card on these bookmarks covers, as
     * its tracker reads it (`AnchorTracker.coveredSpansOver`), for Apply All to read each
     * card over the same spans Apply builds it over.
     *
     * CASES
     *   - a tracker follows these bookmarks -> its spans: its remembered neighbours and its placement
     *   - none does                         -> the spans a tracker that has seen nothing reads: the
     *                                          bookmarks' extent; nothing is kept
     *
     * EXAMPLE
     *   [_pay] over "Notices go to the addresses above. Customer shall pay…" with _pay on the second sentence
     *   -> [{ startOffset: 35, endOffset: 84 }]
     */
    coveredSpansOver(bookmarkNames: readonly string[], rangeOoxml: string): AcceptedTextSpan[] {
        const tracker = this.trackers.get(AnchorTrackerRegistry.keyOf(bookmarkNames)) ?? new AnchorTracker([...bookmarkNames], this.deps, this.config);
        return tracker.coveredSpansOver(rangeOoxml);
    }

    /**
     * One key per bookmark set, whatever order the names come in.
     *
     * EXAMPLE
     *   ["_term", "_pay"] and ["_pay", "_term"] -> "_pay|_term"
     */
    private static keyOf(bookmarkNames: readonly string[]): string {
        return [...bookmarkNames].sort().join("|");
    }

    /** The tracker for these bookmarks, created on first sight; the redline is attached to it once and kept. */
    trackerFor(bookmarkNames: string[], redline: AttachedRedline | null): AnchorTracker {
        const key = AnchorTrackerRegistry.keyOf(bookmarkNames);
        const existing = this.trackers.get(key);
        const tracker = existing ?? new AnchorTracker([...bookmarkNames], this.deps, this.config);
        /**
         * CASES
         *   - existing tracker for key   -> keep it, including its accumulated state
         *   - no tracker for key         -> store this first tracker in the registry
         */
        if (!existing) this.trackers.set(key, tracker);
        /**
         * CASES
         *   - redline is present   -> attach it to this tracker
         *   - redline is null      -> leave the tracker unattached
         */
        if (redline) tracker.attachRedline(redline);
        return tracker;
    }

    /**
     * Where each card of an Apply All run is to be written, from the run's one whole-document
     * read: Word is asked only about the cards the read does not hold, and only by the local
     * recovery steps. The cards those find nothing for, and that the server may place, are
     * left for Apply All's second pass (`placementsFromTheServer`), after this pass's writes.
     *
     * CASES, per card
     *   - the read holds one of its bookmarks           -> found: the placement its tracker holds,
     *                                                      no Word read for it. Its bookmarks that do
     *                                                      not start and end inside one paragraph are
     *                                                      re-laid first, each in a Word write of its
     *                                                      own (`reLayUnsettledBookmarksOf`)
     *   - the read holds none of them                   -> its tracker reconciled under "loss", the
     *                                                      local steps alone: paragraph id, neighbour,
     *                                                      change ledger, the card's anchors, deleted
     *                                                      paragraph; the placement they leave
     *   - those find nothing, and the server may place
     *     it (`AnchorTracker.serverMayPlaceItsCard`)    -> left for the server
     *                                                      (`cardIndexesLeftForTheServer`)
     *   - those find nothing, and every card on its
     *     bookmarks only deletes them or is a comment   -> the placement it holds: the fold reads it
     *                                                      as gone, and the server is never asked
     *   - its redline cannot be read into a tracker     -> on_paragraph; the card fails alone where
     *                                                      it is laid
     *
     * WHY the read decides
     *   A card the read holds is written where its bookmarks stand, so a reconcile of it read
     *   Word for nothing: 13 table cards held "Checking for conflicts" for 20.9 s, and three row
     *   cards 11.7 s, each re-found in Word one at a time (live, Word for Mac, 2026-10-19).
     *
     * WHY the server waits for a second pass
     *   A card asking the server held up every card after it; the cards the read and the
     *   local steps place are written without waiting for it.
     *
     * FALLBACK
     *   The whole-document read throws: every card is reconciled under "apply", the local
     *   steps and then the server, as before the read, and none is left for a second pass.
     *
     * EXAMPLE
     *   cards [A on "_pay", B on "_term", C on "_cap"]; "_term"'s paragraph deleted, "_cap"'s retyped
     *   -> A held by the read: on_paragraph, no Word read
     *   -> B not held: the deleted-paragraph step places it after "5. Payment"
     *   -> C not held, no local step finds it: cardIndexesLeftForTheServer = [2]
     */
    async placementsForApply(cards: CardToApply[]): Promise<ApplyPlacements> {
        const documentPages = await timedBulkApplySpan("first_pass_document_read", () => this.documentPagesForApply(), { cards: cards.length });
        const cardsOfRun: CardOfApplyRun[] = cards.map((card) => ({ card, tracker: this.trackerForApply(card) }));
        const trackersOfRun = cardsOfRun.map((cardOfRun) => cardOfRun.tracker);
        /**
         * FALLBACK
         *   the read threw: every card reconciled under "apply", the local steps and then the
         *   server, as before the read, so none is left for a second pass.
         */
        if (documentPages === null) {
            await this.trackersFoundUnder("apply", trackersOfRun);
            return { placements: AnchorTrackerRegistry.placementsHeldBy(trackersOfRun), cardIndexesLeftForTheServer: [], documentPages: null };
        }
        const cardsHeldByTheRead = new Set(cardsOfRun.filter((cardOfRun) => documentPages.holdsAnyOf(cardOfRun.card.bookmarkNames)));
        const cardsTheReadDoesNotHold = cardsOfRun.filter((cardOfRun) => !cardsHeldByTheRead.has(cardOfRun));
        const edgesReLaid = await timedBulkApplySpan("first_pass_edges_re_laid", () => this.reLayUnsettledBookmarksOf(Array.from(cardsHeldByTheRead, (cardOfRun) => cardOfRun.card), documentPages), { cardsHeldByTheRead: cardsHeldByTheRead.size });
        const trackersFoundByTheLocalSteps = await timedBulkApplySpan("first_pass_local_recovery", () => this.trackersFoundUnder("loss", cardsTheReadDoesNotHold.map((cardOfRun) => cardOfRun.tracker)), { cardsTheReadDoesNotHold: cardsTheReadDoesNotHold.length });
        /**
         * CASES
         *   - an edge was re-laid, or the local steps laid a card's bookmarks back -> read again: the
         *     first read no longer shows those bookmarks where they stand
         *   - nothing was written after the read                                   -> the read the cards
         *     were found on, so the run reads the document once
         */
        const documentPagesAsPlaced = edgesReLaid || trackersFoundByTheLocalSteps.size > 0 ? await timedBulkApplySpan("first_pass_document_read_again", () => this.documentPagesForApply()) : documentPages;
        /**
         * CASES, per card
         *   - held by the read, or found by the local steps                -> placed now
         *   - neither, and the server may place it                         -> left for the server
         *   - neither, and every card on its bookmarks writes nothing back
         *     where they stood (a deletion, a comment)                     -> placed now: the fold reads it as gone
         *   - no tracker: its redline cannot be read                       -> placed now, on_paragraph
         */
        const cardIndexesLeftForTheServer = cardsOfRun.flatMap((cardOfRun, cardIndex) => {
            const { tracker } = cardOfRun;
            const leftForTheServer = tracker !== null && !cardsHeldByTheRead.has(cardOfRun) && !trackersFoundByTheLocalSteps.has(tracker) && tracker.serverMayPlaceItsCard;
            return leftForTheServer ? [cardIndex] : [];
        });
        return { placements: AnchorTrackerRegistry.placementsHeldBy(trackersOfRun), cardIndexesLeftForTheServer, documentPages: documentPagesAsPlaced };
    }

    /**
     * Apply All's second pass: the cards its first pass left for the server
     * (`ApplyPlacements.cardIndexesLeftForTheServer`), each tracker reconciled under "apply",
     * the local steps again and then the server, once the first pass has written.
     */
    async placementsFromTheServer(cards: CardToApply[]): Promise<ServerPlacements> {
        const trackers = cards.map((card) => this.trackerForApply(card));
        const trackersFoundByTheServer = await this.trackersFoundUnder("apply", trackers);
        /**
         * CASES
         *   - the reconcile found the card          -> the placement it left
         *   - it did not, or the card has no tracker -> null
         */
        const placements = trackers.map((tracker) => (tracker !== null && trackersFoundByTheServer.has(tracker) ? tracker.placement : null));
        /**
         * CASES
         *   - the server found a card -> one whole-document read, after it laid the card's bookmarks
         *                                back and after the first pass's writes, for the second fold
         *   - it found none           -> no read: there is nothing to fold
         */
        const documentPages = trackersFoundByTheServer.size > 0 ? await this.documentPagesForApply() : null;
        return { placements, documentPages };
    }

    /**
     * A whole-document read of the run, as pages.
     *
     * NULL
     *   Word threw while reading: logged. On the run's first read every card is reconciled;
     *   on a later one the fold reads each page from Word.
     */
    private async documentPagesForApply(): Promise<BookmarkedPages | null> {
        try {
            const bodyFlatOpc = await timedBulkApplySpan("document_read_from_word", () => this.deps.word.withRead((session) => session.bodyFlatOpc()));
            return timedBulkApplySpanSync("document_read_parsed_into_pages", () => OoxmlRange.bookmarkedPagesOf(bodyFlatOpc), { characters: bodyFlatOpc.length });
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames: [], step: "read document", message: AnchorLog.messageOf(error) });
            return null;
        }
    }

    /**
     * Re-lays every bookmark of these cards that the read holds and that does not start and
     * end inside one paragraph (`BookmarkedPages.unsettledBookmarkNamesOf`): Word's repair of
     * each such bookmark (`DocumentBookmarks.repairEdges`, the step a reconcile starts with),
     * without reconciling the cards.
     *
     * WHY one Word write a bookmark
     *   One card's failure never touches a card that does not depend on it: Word failing
     *   on one bookmark leaves every other bookmark's repair to run.
     *
     * WHEN Word throws on a bookmark
     *   CONSEQUENCE
     *     logged; its card is written over the bookmark as it stands, and the next bookmark
     *     is repaired.
     *
     * RETURNS
     *   whether Word may have re-laid a bookmark: true when it re-laid one, and when it threw
     *   on one part way, so the fold reads the document again.
     *
     * EXAMPLE
     *   row card [_host_blue_land, _daily_pic_angle, _metric_logging_plot], the last grown into the next row
     *   -> repairEdges(["_metric_logging_plot"])
     */
    private async reLayUnsettledBookmarksOf(cards: CardToApply[], documentPages: BookmarkedPages): Promise<boolean> {
        const unsettledBookmarkNames = Array.from(new Set(cards.flatMap((card) => documentPages.unsettledBookmarkNamesOf(card.bookmarkNames))));
        let aBookmarkMayHaveMoved = false;
        for (const bookmarkName of unsettledBookmarkNames) {
            try {
                /** WHEN Word re-laid the bookmark CONSEQUENCE the read no longer shows it where it stands. */
                if (await this.deps.word.withWrite((session) => session.bookmarks.repairEdges([bookmarkName]))) aBookmarkMayHaveMoved = true;
            } catch (error) {
                AnchorLog.record({ kind: "error", bookmarkNames: [bookmarkName], step: "repair edges", message: AnchorLog.messageOf(error) });
                aBookmarkMayHaveMoved = true;
            }
        }
        return aBookmarkMayHaveMoved;
    }

    /**
     * Reconciles each tracker once under `trigger`, all asked for at the same moment: two cards
     * on the same bookmarks share one tracker, and its recovery runs once.
     *
     * WHY all at once and not one after another
     *   A tracker whose bookmarks are lost recovers under the write lock, in the same hold as
     *   every other recovery that asked for the lock before it was granted (`RecoveryBatches`),
     *   so the body is read from Word once for all of them. Awaiting each tracker before asking
     *   for the next made every recovery a hold of its own, and a body read of its own.
     *
     * EXAMPLE
     *   nine cards of a run whose bookmarks were lost -> nine trackers asked together -> one
     *   hold of the write lock, one read of the body (it was nine holds of 1.4 s each)
     *
     * RETURNS
     *   the trackers whose bookmarks the reconcile read, or laid back where recovery found
     *   them. A tracker Word threw for is logged and left out; a null tracker is skipped.
     */
    private async trackersFoundUnder(trigger: RecoveryTrigger, trackers: Array<AnchorTracker | null>): Promise<Set<AnchorTracker>> {
        const foundTrackers = new Set<AnchorTracker>();
        const distinctTrackers = new Set(trackers.filter((tracker): tracker is AnchorTracker => tracker !== null));
        await Promise.all(
            Array.from(distinctTrackers, async (tracker) => {
                try {
                    /** WHEN the reconcile read the bookmarks, or laid them back CONSEQUENCE the tracker is found. */
                    if (await tracker.reconcileToFind(trigger)) foundTrackers.add(tracker);
                } catch (error) {
                    AnchorLog.record({ kind: "error", bookmarkNames: tracker.bookmarkNames, step: "reconcile", message: AnchorLog.messageOf(error) });
                }
            }),
        );
        return foundTrackers;
    }

    /**
     * One placement per card: the one its tracker holds.
     *
     * FALLBACK
     *   a card with no tracker, its redline unreadable -> on_paragraph, the placement a card
     *   holds before any recovery, so the fold lays it and fails it alone.
     */
    private static placementsHeldBy(trackers: Array<AnchorTracker | null>): RedlinePlacement[] {
        return trackers.map((tracker) => tracker?.placement ?? ON_PARAGRAPH);
    }

    /**
     * The tracker of a card, its redline attached. It never throws: one card's failure never
     * stops the other cards being found, and the run fails that card alone.
     *
     * NULL
     *   the redline cannot be read into a tracker (a stored card missing its paragraphs): logged.
     */
    private trackerForApply(card: CardToApply): AnchorTracker | null {
        try {
            return this.trackerFor(card.bookmarkNames, card.redline);
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames: card.bookmarkNames, step: "attach redline", message: AnchorLog.messageOf(error) });
            return null;
        }
    }
}

/** One card Apply is about to write: the bookmarks it targets and its redline, attached to a tracker created for it. */
export interface CardToApply {
    bookmarkNames: string[];
    redline: AttachedRedline | null;
}

/** A card of an Apply All run and the tracker that follows it. */
interface CardOfApplyRun {
    card: CardToApply;
    /** Null when the card's redline cannot be read into a tracker. */
    tracker: AnchorTracker | null;
}

/** Apply All's first pass over its cards (`AnchorTrackerRegistry.placementsForApply`). */
export interface ApplyPlacements {
    /** One placement per card, in the cards' order. */
    placements: RedlinePlacement[];
    /**
     * The cards, by index into the run's cards, that the read does not hold, that no local
     * step found, and that the server may place: Apply All asks the server for them in its
     * second pass (`AnchorTrackerRegistry.placementsFromTheServer`).
     */
    cardIndexesLeftForTheServer: number[];
    /**
     * The whole-document read of the document as this pass leaves it, for the fold to cut its
     * pages from (`LocalBulkApplyInput.documentPagesOfTheRun`): the read the cards were found
     * on when nothing was written after it, else one made after the writes.
     *
     * NULL
     *   Word threw while reading: the fold reads each page from Word.
     */
    documentPages: BookmarkedPages | null;
}

/** Apply All's second pass over the cards its first pass left for the server (`AnchorTrackerRegistry.placementsFromTheServer`). */
export interface ServerPlacements {
    /** One entry per card, in the cards' order: the placement the reconcile left, or null when the server did not find the card either. */
    placements: Array<RedlinePlacement | null>;
    /**
     * A whole-document read made after the server laid the found cards' bookmarks back, for
     * the second pass's fold to cut its pages from.
     *
     * NULL
     *   the server found no card, or Word threw while reading.
     */
    documentPages: BookmarkedPages | null;
}

/**
 * The one Office-backed Word document the trackers and the local Apply All read
 * through; its `events` is the add-in's one stream of document events, registered
 * with Word once for every follower.
 */
export const officeWordDocument = new OfficeWordDocument(DEFAULT_RECOVERY_CONFIG, editedParagraphDetector);

export const anchorTrackers = new AnchorTrackerRegistry(
    { word: officeWordDocument, reanchor: new HttpReanchorClient(), ledger: new DocumentChangeLedger(officeWordDocument, DEFAULT_RECOVERY_CONFIG) },
    DEFAULT_RECOVERY_CONFIG,
);

/** The one page store every chat card and the drawer read the document through: one Word read per document change, shared. */
export const documentPages = new DocumentPageStore(officeWordDocument);

/** What each conversation's agent last saw of the document, and the changes since, ready for the message the user sends next. */
export const agentDocumentViews = new AgentDocumentViews(officeWordDocument, DEFAULT_AGENT_DOCUMENT_VIEWS_CONFIG);
